export interface AcademyTopic {
  term: string;
  definition: string;
  example?: string;
}

export interface PlatformFeature {
  label: string;
  description: string;
  icon: string;
}

export interface PlatformData {
  id: string;
  name: string;
  logo: string;
  color: string;
  tagline: string;
  description: string;
  features: PlatformFeature[];
  bestFor: string[];
}

export interface BrokerConcept {
  title: string;
  description: string;
  icon: string;
  color: string;
  detail: string;
}

export interface XmModuleItem {
  title: string;
  description: string;
  icon: string;
  color: string;
  bullets: string[];
}

export interface ComparisonPrinciple {
  step: string;
  title: string;
  description: string;
  icon: string;
  color: string;
}

export interface AnalysisConcept {
  title: string;
  description: string;
  icon: string;
  color: string;
  concepts: { label: string; detail: string }[];
}

export interface RiskConcept {
  title: string;
  description: string;
  icon: string;
  color: string;
  rule: string;
  detail: string;
}

export interface ToolItem {
  title: string;
  description: string;
  icon: string;
  color: string;
  usage: string;
  tip: string;
}

/* ------------------------------------------------------------------ */
/*  Module 1: Forex Basics                                             */
/* ------------------------------------------------------------------ */

export const forexBasics: AcademyTopic[] = [
  {
    term: "What is Forex?",
    definition: "Forex (Foreign Exchange) is the global decentralized market where currencies are traded. With over $7.5 trillion in daily volume, it is the largest and most liquid financial market in the world — dwarfing the stock market by nearly 30x.",
    example: "When you travel from the US to Europe and exchange USD for EUR, you are participating in the forex market. Institutional traders, central banks, corporations, and retail speculators do this at enormous scale 24 hours a day, 5 days a week.",
  },
  {
    term: "Currency Pairs",
    definition: "Currencies are always traded in pairs — you buy one while simultaneously selling the other. The first currency is the 'base', the second is the 'quote'. If EUR/USD = 1.0850, it means 1 Euro costs 1.0850 US Dollars.",
    example: "Majors (EUR/USD, USD/JPY, GBP/USD, USD/CHF) account for over 70% of all forex volume. Crosses exclude the USD (EUR/GBP, EUR/JPY). Exotics involve one major and one emerging-market currency (USD/TRY, USD/ZAR).",
  },
  {
    term: "Pips & Lots",
    definition: "A pip is the smallest standard price move in forex — typically 0.0001 for most pairs. A lot is the standardized trade size: 1 standard lot = 100,000 units of the base currency. Mini lots (10,000), micro lots (1,000), and nano lots (100) allow smaller position sizing.",
    example: "On EUR/USD with a standard lot, each pip move equals $10. On a micro lot, each pip = $0.10. This is why position sizing is the single most important skill for new traders.",
  },
  {
    term: "Bid, Ask & Spread",
    definition: "The bid is the price at which you can sell; the ask is the price at which you can buy. The spread — the difference between them — is your implicit transaction cost. Tighter spreads mean lower trading costs.",
    example: "If EUR/USD bid = 1.0848 and ask = 1.0850, the spread is 2 pips. This is typical for ECN brokers like IC Markets. A dealing-desk broker might quote 3-4 pips on the same pair during normal hours.",
  },
  {
    term: "Leverage & Margin",
    definition: "Leverage allows you to control a large position with a relatively small deposit (margin). 1:30 leverage means a $1,000 margin controls $30,000. While it amplifies potential profits, it equally amplifies losses — this is the double-edged sword of forex trading.",
    example: "With 1:30 leverage, a 1% move against you wipes out 30% of your margin. With 1:500 leverage (offshore brokers), that same 1% move wipes out 500%. This is why regulated brokers cap leverage for retail clients.",
  },
  {
    term: "Market Sessions",
    definition: "Forex trades 24 hours a day across four major sessions: Sydney (opens the week), Tokyo (Asian session), London (European session — highest volume), and New York (US session). The London-NY overlap (8:00 AM - 12:00 PM EST) sees the most liquidity and tightest spreads.",
    example: "If you trade EUR/USD during the Asian session, spreads are typically wider and price action slower. The same pair during the London-NY overlap offers dramatically tighter spreads and more trading opportunities.",
  },
  {
    term: "Long vs Short",
    definition: "Going long means buying a currency pair expecting it to rise. Going short means selling a pair expecting it to fall. In forex, you can profit in both directions — unlike traditional stock investing where shorting requires borrowing shares.",
    example: "If you believe the Euro will strengthen against the Dollar, you go long EUR/USD. If you believe geopolitical tension will weaken the Euro, you go short EUR/USD. Both trades generate profit if your directional call is correct.",
  },
  {
    term: "Brokers & Liquidity",
    definition: "Retail traders do not access the interbank market directly. Brokers act as intermediaries, aggregating liquidity from Tier-1 banks and offering it to retail clients. ECN/STP brokers pass your orders directly to liquidity providers; Market Makers may take the opposite side of your trade.",
    example: "An ECN broker like Tickmill connects your EUR/USD buy order to liquidity from Barclays, Deutsche Bank, and UBS simultaneously, giving you the best available bid price. A Market Maker might fill your order internally at their quoted price.",
  },
];

/* ------------------------------------------------------------------ */
/*  Module 2: Trading Platform Education                                */
/* ------------------------------------------------------------------ */

export const tradingPlatforms: PlatformData[] = [
  {
    id: "mt4",
    name: "MetaTrader 4",
    logo: "MT4",
    color: "#10B981",
    tagline: "The industry standard — 85%+ of retail forex traders use MT4.",
    description: "Released in 2005, MT4 remains the most widely supported platform in the world. Its strength lies in its massive Expert Advisor (EA) ecosystem, the MQL4 coding language that powers over 10,000+ custom indicators and automated strategies, and near-universal broker support.",
    features: [
      { label: "Expert Advisors (EAs)", description: "Fully automated trading robots that execute strategies 24/5 without human intervention — the backbone of algorithmic retail trading.", icon: "ri-robot-2-line" },
      { label: "Custom Indicators", description: "Over 10,000 community-built indicators available — from simple moving average crossovers to complex harmonic pattern recognition.", icon: "ri-line-chart-line" },
      { label: "MQL4 Language", description: "C-based programming language with extensive documentation. Easier to learn than MQL5, which is why the EA community remains largest on MT4.", icon: "ri-code-line" },
      { label: "9 Timeframes", description: "From 1-minute to monthly charts — sufficient for most trading styles. Lacks tick-level data but covers all standard charting needs.", icon: "ri-time-line" },
      { label: "30+ Built-in Indicators", description: "Includes MACD, RSI, Bollinger Bands, Ichimoku, Fibonacci tools, and all classic technical analysis instruments.", icon: "ri-bar-chart-2-line" },
      { label: "One-Click Trading", description: "Fast order execution with predefined stop-loss and take-profit levels. Essential for scalpers who need sub-second entry.", icon: "ri-flashlight-line" },
    ],
    bestFor: ["Beginner Traders", "EA Developers", "Manual Strategy Traders", "Standard Chart Analysis"],
  },
  {
    id: "mt5",
    name: "MetaTrader 5",
    logo: "MT5",
    color: "#F59E0B",
    tagline: "MT4's successor — multi-asset, faster backtesting, and 21 timeframes.",
    description: "Launched in 2010, MT5 is a significant architectural upgrade over MT4. It supports not only forex but also stocks, futures, and commodities on a single platform. Its 64-bit architecture enables faster backtesting, and the Depth of Market (DOM) feature gives traders visibility into liquidity layers.",
    features: [
      { label: "Multi-Asset Trading", description: "Trade forex, stocks, futures, CFDs, and commodities from a single account — no need to switch between platforms.", icon: "ri-stack-line" },
      { label: "21 Timeframes", description: "From 1-minute to monthly plus 6 intermediate timeframes (M2, M3, M4, M6, M10, M12, H2, H3, H6, H8, H12) — more granular analysis.", icon: "ri-time-line" },
      { label: "MQL5 + 64-bit", description: "Object-oriented MQL5 with faster backtesting (multi-threaded, 64-bit). Larger strategy tester with more accurate tick-level simulation.", icon: "ri-cpu-line" },
      { label: "Depth of Market (DOM)", description: "View full order book liquidity, place limit orders directly on the DOM ladder, and see where the big players are positioned.", icon: "ri-layout-grid-line" },
      { label: "Economic Calendar Built-in", description: "Real-time economic events, forecast vs actual data, and impact ratings — all integrated without third-party plugins.", icon: "ri-calendar-event-line" },
      { label: "38 Built-in Indicators", description: "More indicators than MT4 plus 44 analytical objects for advanced charting. Includes Gann, Elliott Wave, and more tools.", icon: "ri-bar-chart-grouped-line" },
    ],
    bestFor: ["Multi-Asset Traders", "Advanced Analysts", "Stock & Futures Traders", "DOM Scalpers"],
  },
  {
    id: "ctrader",
    name: "cTrader",
    logo: "cT",
    color: "#3B82F6",
    tagline: "The ECN purist's choice — modern UI, Level II pricing, and superior execution transparency.",
    description: "cTrader was built from the ground up for ECN/STP brokers. Its modern, intuitive interface and transparent Level II pricing make it the preferred choice for traders who want to see exactly how their orders are routed. The cAlgo (C#) coding environment attracts professional developers.",
    features: [
      { label: "Level II Pricing", description: "Full market depth display showing all available bid/ask prices from multiple liquidity providers — complete execution transparency.", icon: "ri-eye-line" },
      { label: "cAlgo / C#", description: "Build automated strategies in C# — a modern, widely-used language. Easier for professional developers coming from other industries.", icon: "ri-braces-line" },
      { label: "Modern UI/UX", description: "Clean, detachable panels, customizable layouts, dark mode — a refined interface built for 2020s expectations, not 2005.", icon: "ri-layout-line" },
      { label: "Advanced Order Types", description: "Market Range, Iceberg orders, and advanced OCO (One-Cancels-Other) — tools that MT4/MT5 simply do not offer.", icon: "ri-settings-3-line" },
      { label: "Tick-Level Backtesting", description: "True tick-by-tick historical simulation. Far more accurate than MT4's bar-based backtester for high-frequency strategies.", icon: "ri-history-line" },
      { label: "Cloud-Based", description: "cTrader ID syncs your layouts, watchlists, and settings across devices. Start on desktop, continue on mobile seamlessly.", icon: "ri-cloud-line" },
    ],
    bestFor: ["ECN Purists", "Professional Developers", "Scalpers", "Transparency-Focused Traders"],
  },
  {
    id: "xm-app",
    name: "XM Trading App",
    logo: "XM",
    color: "#EC4899",
    tagline: "Trade on the go — XM's proprietary mobile experience with full account management.",
    description: "XM offers both MT4/MT5 mobile apps and its own branded trading app. The proprietary app integrates account management, deposits, withdrawals, and live chat alongside full trading functionality — so you never need to switch between apps.",
    features: [
      { label: "All-in-One Dashboard", description: "Trade, deposit, withdraw, verify your account, and contact support — all from a single app without app-switching.", icon: "ri-dashboard-line" },
      { label: "Push Notifications", description: "Real-time price alerts, margin call warnings, economic news, and XM bonus/promotion updates delivered instantly.", icon: "ri-notification-3-line" },
      { label: "Biometric Security", description: "Face ID and fingerprint login for instant, secure access. No typing passwords every time you want to check a position.", icon: "ri-fingerprint-line" },
      { label: "MT4/MT5 Integrated", description: "Launch the full MT4 or MT5 mobile experience directly from the XM app. Your credentials sync automatically.", icon: "ri-links-line" },
      { label: "One-Tap Trading", description: "Place market orders with a single tap. Ideal for news traders who need to react in seconds to NFP or FOMC releases.", icon: "ri-cursor-line" },
      { label: "24/7 Live Support", description: "In-app live chat with XM support in 30+ languages. No phone calls, no email tickets — instant help while you trade.", icon: "ri-headphone-line" },
    ],
    bestFor: ["Mobile-First Traders", "XM Account Holders", "News Traders", "Traveling Traders"],
  },
];

/* ------------------------------------------------------------------ */
/*  Module 3: Forex Broker Education                                    */
/* ------------------------------------------------------------------ */

export const brokerConcepts: BrokerConcept[] = [
  {
    title: "What Does a Forex Broker Do?",
    description: "A broker bridges retail traders to the interbank forex market — the network where major banks, hedge funds, and institutions trade currencies. Without a broker, retail traders have no access to currency markets at all.",
    icon: "ri-bank-line",
    color: "#10B981",
    detail: "Brokers aggregate liquidity from Tier-1 providers (Barclays, Deutsche Bank, JPMorgan, UBS, etc.), package it into accessible trading accounts, and provide the platform infrastructure — charts, order execution, margin management, and account services. The quality of a broker directly determines whether your trades are executed at fair market prices or manipulated against you.",
  },
  {
    title: "ECN vs Market Maker — The Critical Distinction",
    description: "ECN (Electronic Communication Network) brokers pass your orders directly to liquidity providers. Market Makers create their own market — they may take the opposite side of your trade, creating a potential conflict of interest.",
    icon: "ri-git-branch-line",
    color: "#F59E0B",
    detail: "ECN/STP brokers earn from commissions and small spreads, with no incentive to see you lose. Market Makers profit from client losses when they act as the counterparty. However, well-regulated Market Makers (like XM under CySEC/FCA) have strict oversight and many traders report excellent execution. The key is regulation — not the model itself.",
  },
  {
    title: "Why Regulation Matters More Than Anything",
    description: "A broker's regulatory status is the single most important factor when choosing where to trade. Top-tier regulators (FCA, ASIC, CySEC) enforce capital requirements, segregated client accounts, negative balance protection, and compensation schemes.",
    icon: "ri-shield-check-line",
    color: "#EC4899",
    detail: "XM is regulated by CySEC (Cyprus), ASIC (Australia), FSC (Belize), and DFSA (Dubai) — a multi-jurisdictional framework. Client funds are held in segregated accounts at Tier-1 banks. The Investor Compensation Fund protects up to €20,000 per client. Compare this with unregulated offshore brokers who offer zero protections — the difference in safety is the difference between a bank vault and a shoebox.",
  },
  {
    title: "Real Cost of Trading — Beyond the Spread",
    description: "The spread is just the visible cost. The real all-in cost includes commissions, swap rates (overnight fees), slippage, and the quality of execution. A broker advertising '0.0 pip spreads' might charge $7 commission per lot — making it more expensive than a 1.0 pip spread broker with no commission.",
    icon: "ri-money-dollar-circle-line",
    color: "#8B5CF6",
    detail: "Example: Broker A offers EUR/USD at 0.1 pip + $7 commission/lot = ~$8 total cost. Broker B offers the same pair at 1.0 pip + $0 commission = ~$10 total cost. On 100 lots, that's $200 difference. But raw cost is not everything — Broker B might offer better execution speed, fewer requotes, and superior customer support. The full picture matters.",
  },
  {
    title: "Account Types — One Size Does Not Fit All",
    description: "Brokers offer different account types for different trader profiles: Micro accounts for beginners, Standard accounts for casual traders, ECN/Zero accounts for professionals, and Islamic (swap-free) accounts for traders who follow Sharia law.",
    icon: "ri-file-list-3-line",
    color: "#F97316",
    detail: "XM offers Micro, Standard, XM Ultra Low, and Shares accounts — each with different minimum deposits (from $5), spreads, leverage caps, and commission structures. A scalper needs ultra-tight spreads on a Zero account. A swing trader holding positions for days might prefer a Standard account with no commissions. Matching the account to your strategy is essential.",
  },
  {
    title: "Deposit & Withdrawal — Speed and Cost",
    description: "The best broker in the world is useless if you cannot easily fund your account or withdraw your profits. Check deposit methods, withdrawal processing times, fees, and whether the broker charges for inactive accounts.",
    icon: "ri-exchange-funds-line",
    color: "#14B8A6",
    detail: "XM processes withdrawals within 24 hours (same-day for most methods) and covers all transfer fees for deposits over $200 — a policy few brokers match. Most competitors take 2-5 business days and pass fees to the client. Fast, free withdrawals are a sign of a broker with healthy cash flow and genuine client-first policies.",
  },
];

/* ------------------------------------------------------------------ */
/*  Module 4: XM Broker Education                                       */
/* ------------------------------------------------------------------ */

export const xmModules: XmModuleItem[] = [
  {
    title: "XM Regulation & Safety",
    description: "Multi-jurisdictional regulation under four respected authorities with segregated client funds and investor compensation protection.",
    icon: "ri-shield-check-line",
    color: "#10B981",
    bullets: [
      "CySEC (Cyprus Securities and Exchange Commission) — License 120/10, EU MiFID II compliant",
      "ASIC (Australian Securities & Investments Commission) — AFSL 443670",
      "FSC (Financial Services Commission, Belize) — License 000261/106",
      "DFSA (Dubai Financial Services Authority) — License F003484",
      "Client funds segregated at Barclays Bank PLC and other Tier-1 institutions",
      "Investor Compensation Fund (ICF) protection up to €20,000 per eligible client",
      "Negative balance protection — you can never lose more than your deposit",
      "Over 5 million clients across 190+ countries since 2009",
    ],
  },
  {
    title: "XM Account Types",
    description: "Four account types designed for every trader profile — from $5 beginners to institutional-grade professionals.",
    icon: "ri-file-list-3-line",
    color: "#F59E0B",
    bullets: [
      "Micro Account: $5 minimum deposit, 1:1000 leverage, micro-lot trading (1,000 units), ideal for beginners",
      "Standard Account: $5 minimum deposit, 1:1000 leverage, standard lots, no commissions — spread-only pricing",
      "XM Ultra Low Account: $50 minimum, spreads from 0.6 pips, 1:500 leverage — the lowest spread XM account",
      "Shares Account: $10,000 minimum, trade real stocks (not CFDs), no leverage, physical share delivery",
      "Islamic (Swap-Free) accounts available on all account types with no overnight interest",
      "Demo accounts: $100,000 virtual funds, unlimited duration, identical to live trading conditions",
    ],
  },
  {
    title: "XM Trading Platforms",
    description: "Full platform ecosystem — MT4, MT5, and the XM proprietary mobile app across desktop, web, and mobile devices.",
    icon: "ri-smartphone-line",
    color: "#3B82F6",
    bullets: [
      "MetaTrader 4: Available for Windows, Mac, iOS, Android, and WebTrader — the global industry standard",
      "MetaTrader 5: Multi-asset platform with 21 timeframes, DOM, and economic calendar built-in",
      "XM Mobile App: Proprietary app with integrated trading, account management, deposits, and 24/7 support",
      "WebTrader: No download required — trade directly from Chrome, Safari, or any modern browser",
      "VPS hosting: Free Virtual Private Server for traders with a minimum balance and monthly volume requirement",
      "All platforms support Expert Advisors (automated trading), custom indicators, and one-click trading",
    ],
  },
  {
    title: "XM Bonus Programs",
    description: "XM offers one of the most competitive bonus structures in the industry — starting with a $30 no-deposit welcome bonus.",
    icon: "ri-gift-line",
    color: "#EC4899",
    bullets: [
      "$30 No-Deposit Bonus: Register, verify your account, get $30 to trade risk-free — profits are withdrawable",
      "50% + 20% Deposit Bonus: Up to $500 on first deposit + 20% on subsequent deposits up to $4,500 total",
      "XM Loyalty Program: Earn XM Points (XMP) on every trade, redeemable for cash credits and bonus funds",
      "Refer-a-Friend: Earn bonuses when your referrals open and fund real trading accounts",
      "Seasonal promotions: Holiday bonuses, trading competitions, and special-event deposit matches",
      "All bonuses are tradable — not just cosmetic. Profits earned from bonus funds are withdrawable after meeting volume requirements",
    ],
  },
  {
    title: "XM Education & Research",
    description: "Comprehensive educational resources — from absolute beginner to advanced strategy, all available free of charge.",
    icon: "ri-book-open-line",
    color: "#F97316",
    bullets: [
      "XM Live Education: Free webinars in 19+ languages hosted by professional traders, multiple sessions daily",
      "Video Tutorials: 200+ on-demand videos covering platform basics, technical analysis, and risk management",
      "Forex Seminars: In-person events in cities worldwide — meet professional traders and analysts face-to-face",
      "Daily Market Analysis: Technical outlooks, fundamental analysis, and trade ideas published every trading day",
      "Economic Calendar: Real-time events with forecast/actual comparisons and volatility impact ratings",
      "Trading Signals: Free MT4/MT5 signals from Trading Central — technical analysis with entry/exit levels",
    ],
  },
  {
    title: "XM Deposit & Withdrawal",
    description: "Fast, free, and flexible funding — XM covers all transfer fees on deposits over $200 and processes withdrawals within 24 hours.",
    icon: "ri-exchange-funds-line",
    color: "#14B8A6",
    bullets: [
      "25+ payment methods: Bank wire, credit/debit cards, Skrill, Neteller, WebMoney, UnionPay, and local bank transfers",
      "Base currencies: USD, EUR, GBP, JPY, CHF, AUD, HUF, PLN, RUB, SGD, ZAR — avoid conversion fees",
      "XM covers ALL transfer fees for deposits of $200 USD or equivalent — zero cost to the client",
      "Withdrawals processed within 24 hours; most e-wallet withdrawals completed same-day",
      "No withdrawal fees from XM's side — the only costs are intermediary bank fees (if applicable)",
      "$5 minimum deposit on Micro and Standard accounts — one of the lowest barriers to entry among regulated brokers",
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Module 5: Broker Comparison Education                               */
/* ------------------------------------------------------------------ */

export const comparisonPrinciples: ComparisonPrinciple[] = [
  {
    step: "01",
    title: "Cost Is the Starting Point — Not the End",
    description: "Raw spreads and commissions matter, but execution quality, slippage, and requotes can silently cost far more than a half-pip spread difference. A broker with a 0.8 pip spread and zero slippage is cheaper than one with 0.3 pip spread and frequent 1-2 pip slippage on entry.",
    icon: "ri-money-dollar-circle-line",
    color: "#10B981",
  },
  {
    step: "02",
    title: "Your Trading Style Dictates the Winner",
    description: "A scalper making 50 trades daily needs ultra-tight spreads and fast execution — cost and speed dominate every other factor. A swing trader holding positions for 3-5 days cares more about swap rates, regulation strength, and platform stability. There is no universally 'best' broker — only the best broker for YOUR strategy.",
    icon: "ri-user-settings-line",
    color: "#F59E0B",
  },
  {
    step: "03",
    title: "Compare What Actually Moves Your P&L",
    description: "Decoration features like '12 account types' or '50+ deposit methods' are noise if the broker has poor execution or hidden fees. Focus on: actual all-in trading cost, execution speed and reliability, withdrawal speed and cost, and regulatory protections. Everything else is secondary.",
    icon: "ri-focus-3-line",
    color: "#8B5CF6",
  },
  {
    step: "04",
    title: "Real Account Data Beats Marketing Claims",
    description: "Every broker's website promises 'tight spreads, fast execution, no requotes.' Our comparison pages test these claims with actual live trading accounts — real money, real execution, real withdrawal tests. If a broker says 0.1 pip spreads but we consistently measure 1.2 pips in live trading, we report that discrepancy.",
    icon: "ri-test-tube-line",
    color: "#EF4444",
  },
  {
    step: "05",
    title: "Regulation Is Non-Negotiable",
    description: "If a broker is not regulated by at least one Tier-1 or Tier-2 authority (FCA, ASIC, CySEC, FSA, etc.), it should not be on your shortlist — regardless of how good their spreads look. No amount of cost savings is worth the risk of losing your entire deposit to an unregulated entity.",
    icon: "ri-shield-flash-line",
    color: "#EC4899",
  },
  {
    step: "06",
    title: "Try Before You Commit",
    description: "Open a demo account, test the platform, contact support with a question, and withdraw a small amount. A broker's true colors show in their support responsiveness and withdrawal processing — not in their marketing. If a broker makes withdrawals difficult or slow, run.",
    icon: "ri-play-circle-line",
    color: "#14B8A6",
  },
];

export const comparisonBrokerCards = [
  {
    name: "Tickmill",
    slug: "/compare/xm-vs-tickmill",
    color: "#10B981",
    logo: "TM",
    highlight: "Lowest all-in cost among all compared brokers on Pro account. Pure ECN execution with zero dealing desk intervention. Best choice for cost-sensitive high-volume traders.",
    bestMetric: "Cost Champion",
    bestValue: "0.1 pip + $2/rt",
  },
  {
    name: "FxPro",
    slug: "/compare/xm-vs-fxpro",
    color: "#F59E0B",
    logo: "FP",
    highlight: "Premium institutional-style broker with FCA regulation. cTrader platform support (XM does not offer cTrader). Best for UK/EU traders wanting FCA protection plus platform choice.",
    bestMetric: "Platform Variety",
    bestValue: "MT4 + MT5 + cTrader",
  },
  {
    name: "IC Markets",
    slug: "/compare/xm-vs-icmarkets",
    color: "#3B82F6",
    logo: "IC",
    highlight: "The go-to broker for automated traders and EA developers. Raw Spread account offers true ECN pricing from 0.0 pips. Superior for scalpers and algo-trading strategies.",
    bestMetric: "Algo-Trading King",
    bestValue: "0.0 pip + $3.5/rt",
  },
  {
    name: "Axi",
    slug: "/compare/xm-vs-axi",
    color: "#EF4444",
    logo: "AX",
    highlight: "Strong ASIC regulation with competitive pricing. Known for excellent customer service and the popular Axi Select funded trader program. Good alternative for Australian traders.",
    bestMetric: "Funded Trader Choice",
    bestValue: "Axi Select Program",
  },
  {
    name: "FXTM",
    slug: "/compare/xm-vs-fxtm",
    color: "#EC4899",
    logo: "FT",
    highlight: "Great educational resources and strong brand presence in emerging markets. Competitive bonus programs and loyalty rewards. Strong choice for traders who value education and support.",
    bestMetric: "Education Leader",
    bestValue: "200+ free webinars/yr",
  },
];

export const keyComparisonDimensions = [
  { label: "Trading Cost", weight: "30%", description: "Spread + commission + swap rates + slippage = real all-in cost", icon: "ri-money-dollar-circle-line" },
  { label: "Regulation & Safety", weight: "25%", description: "Which regulators? Segregated funds? Compensation scheme? Years in operation?", icon: "ri-shield-check-line" },
  { label: "Execution Quality", weight: "20%", description: "Speed, requotes, slippage, and whether the broker operates ECN/STP or Market Maker", icon: "ri-flashlight-line" },
  { label: "Platforms & Tools", weight: "10%", description: "MT4, MT5, cTrader? Proprietary app? VPS hosting? Trading Central integration?", icon: "ri-smartphone-line" },
  { label: "Deposits & Withdrawals", weight: "10%", description: "Processing speed, fees (who covers them?), method variety, base currencies", icon: "ri-exchange-funds-line" },
  { label: "Education & Support", weight: "5%", description: "Webinars, video tutorials, market analysis, 24/7 support availability and quality", icon: "ri-book-open-line" },
];

/* ------------------------------------------------------------------ */
/*  Module 6: Forex Analysis Education                                  */
/* ------------------------------------------------------------------ */

export const analysisConcepts: AnalysisConcept[] = [
  {
    title: "Technical Analysis",
    description: "The study of price action, chart patterns, and indicators to forecast future price movements based on historical data.",
    icon: "ri-line-chart-line",
    color: "#10B981",
    concepts: [
      { label: "Support & Resistance", detail: "Price levels where buying (support) or selling (resistance) pressure consistently appears. These are not exact lines — they are zones. The more times a level is tested, the stronger it becomes." },
      { label: "Trend Analysis", detail: "Higher highs + higher lows = uptrend. Lower highs + lower lows = downtrend. Equal highs and lows = range. The trend is your friend until the structure breaks." },
      { label: "Candlestick Patterns", detail: "Individual and multi-candle formations that signal potential reversals or continuations — Doji, Hammer, Engulfing, Morning Star, and others. Context matters more than the pattern itself." },
      { label: "Indicators", detail: "Lagging (moving averages, MACD) confirm trends. Leading (RSI, Stochastic) predict reversals. Volume indicators (OBV) confirm participation. No single indicator is sufficient alone." },
      { label: "Chart Patterns", detail: "Head & Shoulders, Double Top/Bottom, Triangles, Flags, Wedges — recognizable formations that statistically resolve in predictable directions with measurable targets." },
    ],
  },
  {
    title: "Fundamental Analysis",
    description: "The study of economic, political, and social forces that drive currency supply and demand — the macro forces behind every price move.",
    icon: "ri-newspaper-line",
    color: "#F59E0B",
    concepts: [
      { label: "Interest Rates", detail: "The single most powerful driver of currency value. Higher rates attract foreign capital → stronger currency. Central bank rate decisions and forward guidance are the biggest market-moving events." },
      { label: "Economic Indicators", detail: "GDP growth, CPI inflation, employment (NFP, unemployment rate), retail sales, PMI manufacturing — each reveals a piece of the economic health puzzle that determines currency strength." },
      { label: "Central Bank Policy", detail: "Fed (USD), ECB (EUR), BOJ (JPY), BOE (GBP) — each central bank's monetary policy stance (hawkish/dovish) directly drives their currency. Speeches and meeting minutes create massive volatility." },
      { label: "Geopolitical Events", detail: "Elections, trade wars, sanctions, conflicts — sudden events that shift risk sentiment and capital flows. Safe-haven currencies (USD, JPY, CHF) rally during uncertainty; risk currencies sell off." },
      { label: "Correlation Analysis", detail: "Some currencies move together (EUR/USD and GBP/USD are positively correlated). Others move opposite (USD/CHF and EUR/USD). Understanding correlations prevents doubling your risk accidentally." },
    ],
  },
  {
    title: "Sentiment Analysis",
    description: "Measuring the crowd's mood — are most traders bullish or bearish? Extreme sentiment often signals impending reversals because the market cannot sustain one-sided positioning.",
    icon: "ri-emotion-line",
    color: "#3B82F6",
    concepts: [
      { label: "COT Report", detail: "Commitment of Traders — a weekly CFTC report showing positioning of commercial hedgers, large speculators, and small traders. Extreme net-long positioning often precedes reversals." },
      { label: "Broker Sentiment", detail: "Many brokers publish the percentage of their clients long vs short on each pair. Extreme readings (80%+ long) are contrarian signals — the crowd is usually wrong at turning points." },
      { label: "Fear & Greed Index", detail: "Measures market emotion across volatility, put/call ratios, safe-haven demand, and other metrics. Extreme fear = potential buying opportunity; extreme greed = caution warranted." },
      { label: "News Sentiment", detail: "Automated sentiment scoring of financial news headlines — positive, neutral, or negative tone toward a currency. Aggregated over time, this provides a powerful macro sentiment overlay.", icon: "ri-emotion-line" },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Module 7: Risk Management Education                                 */
/* ------------------------------------------------------------------ */

export const riskConcepts: RiskConcept[] = [
  {
    title: "The 1% Rule",
    description: "Never risk more than 1% of your account balance on any single trade. This is the single most important rule in trading — more important than any entry strategy, indicator, or analysis technique.",
    icon: "ri-percent-line",
    color: "#10B981",
    rule: "Max risk per trade = Account Balance × 0.01",
    detail: "On a $10,000 account, your maximum loss per trade should be $100. If your stop-loss is 20 pips away and you are trading standard lots ($10/pip), your max position is 0.5 lots. At 10 pips stop-loss, it is 1.0 lots. The math is simple, but the discipline is hard — and it separates surviving traders from the 70%+ who blow their accounts within 90 days.",
  },
  {
    title: "Risk-to-Reward Ratio (R:R)",
    description: "For every dollar you risk, how much do you stand to gain? A minimum 1:2 R:R means you can be wrong 50% of the time and still be profitable. Most losing traders have R:R below 1:1.",
    icon: "ri-scales-line",
    color: "#F59E0B",
    rule: "Minimum 1:2 R:R — risk 1 to make 2",
    detail: "Trade A: Risk $50, potential reward $150 = 1:3 R:R. Trade B: Risk $50, potential reward $30 = 1:0.6 R:R. Even with 60% win rate on Trade B vs 40% on Trade A, Trade A's strategy produces higher net profit over 100 trades. Win rate without proper R:R is a vanity metric.",
  },
  {
    title: "Position Sizing",
    description: "How many lots you trade determines how much you risk. Position size is a function of: account size, risk percentage, and stop-loss distance. It is a math problem, not a gut feeling.",
    icon: "ri-calculator-line",
    color: "#8B5CF6",
    rule: "Position size = (Account × Risk%) ÷ (Stop Loss in pips × Pip Value)",
    detail: "$5,000 account, 1% risk ($50), 25 pip stop-loss on EUR/USD mini lots ($1/pip): Position = 50 ÷ (25 × 1) = 2 mini lots. If your stop-loss widens to 50 pips, position halves to 1 mini lot. Larger stop → smaller position. This is the math that keeps you in the game.",
  },
  {
    title: "Stop-Loss Orders — Non-Negotiable",
    description: "Every single trade must have a stop-loss. No exceptions. A stop-loss is not an admission of being wrong — it is a pre-planned exit that protects your capital from catastrophic losses.",
    icon: "ri-shield-flash-line",
    color: "#EF4444",
    rule: "Set SL before entry. Never widen. Never remove.",
    detail: "The most dangerous words in trading: 'It will come back.' A trade without a stop-loss is not a trade — it is gambling. A 50-pip stop can save you from a 500-pip loss when unexpected news hits. Swiss Franc Black Swan (2015): Traders without stops lost entire accounts in minutes. Those with stops survived to trade another day.",
  },
  {
    title: "Correlation Risk",
    description: "Opening multiple positions in positively correlated pairs (EUR/USD + GBP/USD long simultaneously) accidentally doubles your risk. Treat correlated positions as a single aggregate exposure.",
    icon: "ri-links-line",
    color: "#EC4899",
    rule: "Aggregate risk across correlated pairs ≤ 2-3% total",
    detail: "EUR/USD and GBP/USD have a 0.85+ positive correlation. Being long both means you are effectively doubling down on the same trade. If the dollar strengthens, both positions lose simultaneously. Check correlation matrices before entering multiple positions. Diversify across uncorrelated or negatively correlated pairs.",
  },
  {
    title: "The Drawdown Recovery Trap",
    description: "The deeper the drawdown, the exponentially harder it is to recover. A 10% loss requires an 11% gain. A 50% loss requires a 100% gain just to break even. Prevention is infinitely easier than recovery.",
    icon: "ri-error-warning-line",
    color: "#F97316",
    rule: "Drawdown cap: 20% max — stop trading if breached",
    detail: "Drawdown-to-Recovery Table: 10% loss → 11% gain needed. 20% → 25%. 30% → 43%. 40% → 67%. 50% → 100%. 60% → 150%. 70% → 233%. 80% → 400%. 90% → 900%. This is not linear — it is exponential. Capping drawdown at 20% means you never need more than a 25% return to recover.",
  },
];

/* ------------------------------------------------------------------ */
/*  Module 8: Forex Tools                                              */
/* ------------------------------------------------------------------ */

export const forexTools: ToolItem[] = [
  {
    title: "Pip Calculator",
    description: "Know exactly how much each pip movement is worth in your account currency before placing a trade. Essential for accurate position sizing and risk calculation.",
    icon: "ri-calculator-line",
    color: "#10B981",
    usage: "Enter your pair, lot size, and account currency — the calculator shows pip value instantly. A standard lot on EUR/USD: $10/pip. On GBP/JPY in mini lots: ¥100/pip.",
    tip: "Always calculate pip value BEFORE opening a position. A trade that looks like a $50 risk might actually be $80 if you miscalculated pip value on a cross pair.",
  },
  {
    title: "Position Size Calculator",
    description: "Enter your account balance, risk percentage, and stop-loss distance — the calculator tells you exactly how many lots to trade. Removes emotion from position sizing.",
    icon: "ri-ruler-line",
    color: "#F59E0B",
    usage: "Account: $5,000 | Risk: 1% | Stop: 20 pips → Position: 2.5 mini lots. Account: $500 | Risk: 2% | Stop: 15 pips → Position: 0.67 micro lots.",
    tip: "Use this before EVERY trade — not just when you are unsure. Consistent position sizing is the foundation of a profitable trading career.",
  },
  {
    title: "Currency Converter",
    description: "Real-time conversion between any two currencies at live interbank rates. Useful for understanding cross-rates and calculating profits in your home currency.",
    icon: "ri-exchange-line",
    color: "#3B82F6",
    usage: "Convert your trading profits from the quote currency to your home currency. If your account is in USD but you traded EUR/GBP, convert GBP profit to USD instantly.",
    tip: "Remember: the rate you see on Google or XE.com is the mid-market rate — your broker will have a small spread on top. Use this for estimation, not exact execution pricing.",
  },
  {
    title: "Economic Calendar",
    description: "Stay ahead of market-moving events. Filter by currency, impact level, and date range. Never get caught off-guard by NFP, FOMC, or ECB announcements again.",
    icon: "ri-calendar-event-line",
    color: "#EC4899",
    usage: "Check the calendar before every trading session. Red (high impact) events typically cause 30-80+ pip moves. Avoid trading 15 minutes before and after high-impact news unless your strategy specifically trades news events.",
    tip: "The actual number matters less than the difference between forecast and actual. A 'bad' NFP number can be bullish if the market expected even worse.",
  },
  {
    title: "Margin Calculator",
    description: "Calculate the exact margin required to open and maintain a position. Understand how leverage affects your capital requirements and avoid margin calls.",
    icon: "ri-funds-line",
    color: "#8B5CF6",
    usage: "1 standard lot EUR/USD at 1:30 leverage → ~€3,617 margin required. The same position at 1:500 leverage → ~€217 margin. Higher leverage = lower margin = higher risk of over-leveraging.",
    tip: "More available margin is not always better. Just because you CAN open 10 lots does not mean you SHOULD. Use margin as a safety buffer, not a capacity indicator.",
  },
  {
    title: "Profit Calculator",
    description: "Input your entry price, exit price, position size, and pair — instantly see your profit or loss in both pips and your account currency.",
    icon: "ri-line-chart-line",
    color: "#F97316",
    usage: "Bought 2 standard lots EUR/USD at 1.0850, sold at 1.0920 = +70 pips × $20/pip = $1,400 profit. Bought 0.5 lots at 1.0850, stopped out at 1.0810 = -40 pips × $5/pip = -$200 loss.",
    tip: "Review your past trades with this tool. Understanding the actual dollar impact of your wins and losses — not just pips — gives you a clearer picture of your strategy's real performance.",
  },
  {
    title: "Volatility Calculator",
    description: "Measure a pair's average true range (ATR) and historical volatility. Trade pairs with appropriate volatility for your strategy — not all pairs behave the same way.",
    icon: "ri-pulse-line",
    color: "#14B8A6",
    usage: "EUR/USD ATR(14) ≈ 60 pips/day — suitable for day trading. GBP/JPY ATR(14) ≈ 150 pips/day — high volatility, wider stops required. Exotics can move 200-500 pips/day — very high risk.",
    tip: "Match your stop-loss and take-profit levels to the pair's actual volatility. A 20-pip stop on GBP/JPY is essentially random noise and will get hit constantly.",
  },
  {
    title: "Swap Rate Calculator",
    description: "Calculate overnight holding costs (swap/rollover) for any position. Critical for swing and position traders who hold trades for days or weeks.",
    icon: "ri-moon-line",
    color: "#A855F7",
    usage: "Long EUR/USD: typically negative swap (you pay). Short EUR/USD: typically positive swap (you earn). Long 1 lot for 30 days at -$8/day = -$240 in swap costs alone — significant for position traders.",
    tip: "Wednesday night is triple swap day for most pairs (covers the weekend). Factor triple swaps into your calculations when holding through Wednesday rollover.",
  },
];